import type {
  AnswersRevealedPayload,
  GameEndedPayload,
  GameState,
  LeaderboardEntry,
  QuestionStartedPayload,
} from '@arabquiz/api-client'

export type GamePhase = 'idle' | 'lobby' | 'question' | 'results' | 'leaderboard' | 'final' | 'error'

export interface GameStateData {
  phase: GamePhase
  gameId: string | null
  pin: string | null
  playerCount: number
  /**
   * Authoritative list of currently-connected players, maintained by the
   * `rosterUpdated` event. Lets the host lobby show names, not just a count.
   * Reset to [] on RESET. Count is derived from this on the server side and
   * always arrives together in the same payload.
   */
  players: { playerId: string; nickname: string }[]
  playerId: string | null
  playerToken: string | null
  nickname: string | null
  score: number
  currentQuestion: QuestionStartedPayload | null
  correctAnswer: string | null
  /**
   * Explanation for the correct answer, set by `answersRevealed` and shown on
   * the results screen. Server-sourced, surfaced only after reveal; reset to
   * null on each new question. May be multilingual — the view filters it to
   * Arabic-only before display.
   */
  explanation: string | null
  leaderboard: LeaderboardEntry[]
  currentStreak: number
  /**
   * Running count of player answer submissions the server has acknowledged
   * for the **current** question. Hosts display this against `playerCount`
   * (e.g. "12 / 30") so they know when it is safe to call `RevealAnswers`.
   * Always reset to 0 on `QUESTION_STARTED` — the server scopes its
   * `answerCount` field to the active question, but rather than trust the
   * first emission to be the absolute count we re-anchor on every new
   * question to keep the local view consistent across reconnects.
   */
  answersReceivedCount: number
  /**
   * The question index this player has already submitted an answer for, or
   * null. Set when the player submits, and — crucially — re-asserted by the
   * server's `alreadyAnswered` event after a mid-question reconnect, so a
   * player who refreshes after answering sees the "submitted" state instead of
   * being able to answer again. Reset to null on each new `QUESTION_STARTED`.
   */
  answeredQuestionIndex: number | null
  /**
   * Server-authoritative per-question result for THIS player, set by the
   * `answerResult` event the hub sends to each player on reveal. Drives the
   * results screen's correct/points feedback. Sourcing it from the server (not
   * a client-side score delta) keeps it accurate across a mid-round refresh.
   */
  lastResult: { questionIndex: number; points: number; isCorrect: boolean } | null
  error: string | null
  isHost: boolean
  /**
   * When non-null, the host has been prompted to extend the session and has
   * this many seconds to respond before it auto-expires. Drives the extend
   * modal; cleared on extend/reset.
   */
  expiryPromptSeconds: number | null
  /**
   * Whether the host is currently connected. Set false by `hostDisconnected`
   * and true by `hostReconnected`; drives the player-side "waiting for host"
   * overlay. Defaults true and is reset true on any terminal/exit transition so
   * the overlay never lingers past game end or error.
   */
  hostConnected: boolean
}

export type GameAction =
  | { type: 'HOST_JOINED'; gameId: string; pin: string; playerCount: number }
  | { type: 'PLAYER_JOINED'; gameId: string | null; playerId: string; token: string; nickname: string; playerCount?: number | undefined }
  | { type: 'RESTORE_PLAYER_SESSION'; gameId: string; token: string; nickname: string }
  | { type: 'ROSTER_UPDATED'; players: { playerId: string; nickname: string }[]; playerCount: number }
  | { type: 'PLAYER_COUNT_UPDATED'; playerCount: number }
  | { type: 'QUESTION_STARTED'; payload: QuestionStartedPayload }
  | { type: 'ANSWER_RECEIVED'; points: number; isCorrect: boolean }
  | { type: 'ANSWER_SUBMITTED'; questionIndex: number }
  | { type: 'ALREADY_ANSWERED'; questionIndex: number }
  | { type: 'ANSWER_RESULT'; questionIndex: number; points: number; isCorrect: boolean; totalScore: number }
  | { type: 'ANSWER_COUNT_UPDATED'; count: number }
  | { type: 'ANSWERS_REVEALED'; payload: AnswersRevealedPayload }
  | { type: 'GAME_ENDED'; payload: GameEndedPayload }
  | { type: 'RECONNECTED'; score: number; state: GameState; currentQuestionIndex: number }
  | { type: 'ERROR'; message: string }
  | { type: 'SESSION_EXPIRING'; respondWithinSeconds: number }
  | { type: 'SESSION_EXTENDED' }
  | { type: 'HOST_DISCONNECTED' }
  | { type: 'HOST_RECONNECTED' }
  | { type: 'RESET' }

export const initialGameState: GameStateData = {
  phase: 'idle',
  gameId: null,
  pin: null,
  playerCount: 0,
  players: [],
  playerId: null,
  playerToken: null,
  nickname: null,
  score: 0,
  currentQuestion: null,
  correctAnswer: null,
  explanation: null,
  leaderboard: [],
  currentStreak: 0,
  answersReceivedCount: 0,
  answeredQuestionIndex: null,
  lastResult: null,
  error: null,
  isHost: false,
  expiryPromptSeconds: null,
  hostConnected: true,
}

function mapServerStateToPhase(state: GameState): GamePhase {
  switch (state) {
    case 'Lobby':
      return 'lobby'
    case 'InQuestion':
      return 'question'
    case 'ShowingResults':
      return 'results'
    case 'ShowingLeaderboard':
      return 'leaderboard'
    case 'Final':
      return 'final'
    default:
      return 'idle'
  }
}

export function gameReducer(state: GameStateData, action: GameAction): GameStateData {
  switch (action.type) {
    case 'HOST_JOINED': {
      // A host re-join (e.g. after a transient websocket drop triggers
      // replayRejoinIntent) must NOT downgrade an in-progress game back to the
      // lobby. Only the initial join — from idle/lobby — sets phase to 'lobby';
      // mid-game we preserve the current phase so the host stays on the right
      // screen. The backend re-sends the live question on reconnect so the UI
      // restores correctly.
      const inProgress = state.phase !== 'idle' && state.phase !== 'lobby'
      return {
        ...state,
        phase: inProgress ? state.phase : 'lobby',
        gameId: action.gameId,
        pin: action.pin,
        playerCount: action.playerCount,
        error: null,
        isHost: true,
      }
    }
    case 'PLAYER_JOINED':
      return {
        ...state,
        phase: 'lobby',
        // The hub's SubmitAnswer/Reconnect take the game's GUID, not the PIN.
        // Carry it into state on join (decoded from the player token) so the
        // player can actually submit answers — without it, `gameRef` falls
        // back to '' and SignalR can't bind the Guid argument.
        gameId: action.gameId,
        playerId: action.playerId,
        playerToken: action.token,
        nickname: action.nickname,
        playerCount: action.playerCount ?? state.playerCount,
        error: null,
      }
    case 'RESTORE_PLAYER_SESSION':
      return {
        ...state,
        gameId: action.gameId,
        playerToken: action.token,
        nickname: action.nickname,
        error: null,
      }
    case 'ROSTER_UPDATED':
      return { ...state, players: action.players, playerCount: action.playerCount }
    case 'PLAYER_COUNT_UPDATED':
      return { ...state, playerCount: action.playerCount }
    case 'QUESTION_STARTED':
      // Re-anchor the answer-count counter to zero so the host UI doesn't
      // show stale "12 / 30" copy from the previous question while the
      // first answer of the new question is still in flight.
      //
      // Crucially we only CLEAR the per-player "already answered" / result
      // markers when this is a genuinely NEW question index. On a mid-question
      // reconnect the server re-sends `questionStarted` for the SAME index the
      // player already answered; blindly resetting here would wipe the
      // already-answered state and let them answer twice (or hide the
      // "submitted" badge). Keying the reset on an index change makes the
      // restore order-independent — it no longer matters whether the
      // `alreadyAnswered` / `answerResult` events arrive before or after.
      {
        const sameQuestion = state.answeredQuestionIndex === action.payload.questionIndex
        const keepResult =
          state.lastResult && state.lastResult.questionIndex === action.payload.questionIndex
        return {
          ...state,
          phase: 'question',
          currentQuestion: action.payload,
          correctAnswer: null,
          explanation: null,
          answersReceivedCount: 0,
          answeredQuestionIndex: sameQuestion ? state.answeredQuestionIndex : null,
          lastResult: keepResult ? state.lastResult : null,
          error: null,
        }
      }
    case 'ANSWER_RECEIVED':
      return {
        ...state,
        score: state.score + action.points,
        currentStreak: action.isCorrect ? state.currentStreak + 1 : 0,
      }
    case 'ANSWER_SUBMITTED':
      return { ...state, answeredQuestionIndex: action.questionIndex }
    case 'ALREADY_ANSWERED':
      // Server told us (on reconnect) that this question was already answered.
      return { ...state, answeredQuestionIndex: action.questionIndex }
    case 'ANSWER_RESULT':
      // Server-authoritative per-question outcome, sent to each player on
      // reveal. Overwrites score with the server total (correct even after a
      // mid-round refresh) and records the per-question result for the screen.
      return {
        ...state,
        score: action.totalScore,
        lastResult: {
          questionIndex: action.questionIndex,
          points: action.points,
          isCorrect: action.isCorrect,
        },
      }
    case 'ANSWER_COUNT_UPDATED':
      return { ...state, answersReceivedCount: action.count }
    case 'ANSWERS_REVEALED':
      return {
        ...state,
        phase: 'results',
        correctAnswer: action.payload.correctAnswer,
        explanation: action.payload.explanation ?? null,
        leaderboard: action.payload.leaderboard,
      }
    case 'GAME_ENDED':
      return { ...state, phase: 'final', leaderboard: action.payload.leaderboard, hostConnected: true }
    case 'RECONNECTED':
      return {
        ...state,
        phase: mapServerStateToPhase(action.state),
        score: action.score,
        currentStreak: 0,
        error: null,
        hostConnected: true,
      }
    case 'ERROR':
      return { ...state, phase: 'error', error: action.message, expiryPromptSeconds: null, hostConnected: true }
    case 'SESSION_EXPIRING':
      return { ...state, expiryPromptSeconds: action.respondWithinSeconds }
    case 'SESSION_EXTENDED':
      return { ...state, expiryPromptSeconds: null }
    case 'HOST_DISCONNECTED':
      return { ...state, hostConnected: false }
    case 'HOST_RECONNECTED':
      return { ...state, hostConnected: true }
    case 'RESET':
      return initialGameState
    default:
      return state
  }
}
